import { useMemo } from 'react'
import { isAfter } from 'date-fns'
import { theatricalGoalMilestones } from '@/constants/theatricalGoalMilestones'
import { getWebClient } from '@/services/ApolloClient'
import { useGoal } from '@/services/GoalService'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'
import { getDayBefore } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/utils/'

export interface GoalLineInfo {
  ticketsSold: number
  goalLineTitle: string
  milestonePercentage: number
  currentMilestone: number
  isLastMilestone: boolean
  kidTicketsSold: Maybe<string>
}

function isLastOfArray(element: number, array: number[]) {
  if (Array.isArray(array) && array.length > 0) {
    return element === array[array.length - 1]
  }
  return false
}

function getCurrentMilestone(ticketsSold: number, milestones: number[]) {
  const currentUsGoalMilestone = milestones?.find((m: number, index: number) => {
    if (m > ticketsSold) return m
    if (milestones.length - 1 === index) return m
  })
  return currentUsGoalMilestone ?? 0
}

function getMilestonePercentage(ticketsSold: number, currentMilestone: number) {
  const noMilestonePercentage = ticketsSold === 0 && currentMilestone === 0
  return noMilestonePercentage ? 0 : Math.floor((ticketsSold / currentMilestone) * 100)
}

interface Props {
  releaseDate: string
  theatricalSlug: Maybe<string>
  isPossumTrotExperiment?: boolean
  usePifGoal?: boolean
}
const useTheatricalGoalService = ({ releaseDate, theatricalSlug, isPossumTrotExperiment, usePifGoal }: Props) => {
  const { t } = useTranslate('common')
  const client = getWebClient()
  const milestones = useMemo(() => {
    if (isPossumTrotExperiment) return [1000000]
    if (!theatricalSlug) return theatricalGoalMilestones.default
    return theatricalGoalMilestones[theatricalSlug] ?? theatricalGoalMilestones.default
  }, [isPossumTrotExperiment, theatricalSlug])

  const { raisedRaw } = useGoal({
    slug: theatricalSlug ?? '',
    client,
    // For David project with PIF goal flag, use hardcoded goal id 274 instead of activeGoal
    goalId: usePifGoal && theatricalSlug === 'david' ? '274' : undefined,
  })
  const { raised: kidTicketsSold } = useGoal({
    slug: theatricalSlug ?? '',
    client,
    goalId: '274',
  })

  const goalLineTitle = useMemo(() => {
    if (theatricalSlug === 'david') return 'Goal: 1 Million Tickets for 1 Million Kids'
    if (!releaseDate) return t('totalPreSales', 'Total Pre Sales')
    const isInBoxOffice = isAfter(new Date(), getDayBefore(new Date(releaseDate)))
    if (isInBoxOffice) return t('totalBoxOffice', 'Total Box Office')
    return t('totalPreSales', 'Total Pre Sales')
  }, [t, releaseDate, theatricalSlug])

  const goalLineInfo: GoalLineInfo = useMemo(() => {
    const ticketsSold = raisedRaw ? Math.round(raisedRaw / 100 / 14) : 0
    const currentMilestone = getCurrentMilestone(ticketsSold, milestones)
    const milestonePercentage = getMilestonePercentage(ticketsSold, currentMilestone)

    return {
      ticketsSold,
      goalLineTitle,
      milestonePercentage,
      isLastMilestone: isLastOfArray(currentMilestone, milestones),
      currentMilestone,
      kidTicketsSold,
    }
  }, [goalLineTitle, kidTicketsSold, milestones, raisedRaw])

  return { goalLineInfo }
}

export default useTheatricalGoalService
